import { useState, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Mail, Inbox, Send, CheckCircle, AlertCircle, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { defaultStagger } from '@tendbot/ui'
import { useAsyncData } from '../../shared/hooks/useAsyncData'
import { SkeletonListItem } from '@tendbot/ui'
import { relativeTime } from '@tendbot/ui'
import { getEmails, type EmailItem, type IncomingEmail, type SentEmail } from './api'
import EmailDetailSheet from './EmailDetailSheet'
import type { DeepLink } from '../../shared/types/navigation'

type MailTab = 'inbox' | 'sent'

function isIncoming(email: EmailItem): email is IncomingEmail {
  return email.direction === 'incoming'
}

function isSent(email: EmailItem): email is SentEmail {
  return email.direction === 'outgoing'
}

function EmailRow({ email, onClick }: { email: EmailItem; onClick: () => void }) {
  const sent = isSent(email)
  const incoming = isIncoming(email) ? email : null
  const unread = incoming && !incoming.read

  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-3 rounded-xl border border-gold/10 bg-charcoal/50 hover:bg-charcoal/80 hover:border-gold/20 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          sent ? 'bg-emerald/10' : incoming?.fromSelf ? 'bg-pearl/10' : 'bg-gold/10'
        }`}>
          {sent
            ? <Send size={14} className="text-emerald/70" />
            : incoming?.fromSelf
              ? <User size={14} className="text-pearl/50" />
              : <Inbox size={14} className="text-gold/70" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className={`text-sm truncate ${unread ? 'text-ivory font-medium' : 'text-ivory/80'}`}>
                {sent ? (email as SentEmail).to.name : (email as IncomingEmail).from.name}
              </span>
              {incoming?.importance === 'high' && (
                <AlertCircle size={12} className="text-amber/70 flex-shrink-0" />
              )}
            </div>
            <span className="text-xs text-pearl/60 flex-shrink-0">{relativeTime(email.timestamp)}</span>
          </div>
          <p className="text-xs text-pearl/60 truncate mt-0.5">{email.subject}</p>
          <p className="text-xs text-pearl/60 truncate mt-0.5">{email.preview}</p>
        </div>
        {sent && (
          <span className="flex-shrink-0">
            <CheckCircle size={12} className="text-emerald/50" />
          </span>
        )}
        {unread && (
          <span className="w-2 h-2 rounded-full bg-gold flex-shrink-0" />
        )}
      </div>
    </button>
  )
}

export default function MailScreen({
  onNavigate,
}: {
  onNavigate?: (tab: string, params?: DeepLink | null) => void
}) {
  const { t } = useTranslation('mail')
  const { container, item } = defaultStagger
  const [tab, setTab] = useState<MailTab>('inbox')
  const [selectedEmail, setSelectedEmail] = useState<EmailItem | null>(null)

  const direction = tab === 'sent' ? 'outgoing' : 'incoming'
  const fetchEmails = useCallback(() => getEmails(direction), [direction])
  const { data, loading } = useAsyncData(fetchEmails, [direction])

  const emails = data?.emails ?? []
  const stats = data?.stats

  const tabs: { id: MailTab; label: string; icon: typeof Inbox; count?: number }[] = useMemo(() => [
    { id: 'inbox', label: t('tabs.inbox'), icon: Inbox, count: tab === 'inbox' ? stats?.unread : undefined },
    { id: 'sent', label: t('tabs.sent'), icon: Send, count: tab === 'sent' ? stats?.total : undefined },
  ], [t, tab, stats])

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="visible"
      className="h-full flex flex-col overflow-hidden"
    >
      {/* Header + tabs */}
      <motion.div variants={item} className="px-5 sm:px-8 lg:px-12 pt-4 pb-2">
        <div className="max-w-2xl mx-auto">
          <h1 className="font-display text-xl text-ivory font-semibold mb-3">{t('title')}</h1>
          <div className="flex gap-1 p-1 rounded-xl bg-charcoal/60 border border-gold/10">
            {tabs.map(({ id, label, icon: Icon, count }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                  tab === id
                    ? 'bg-gold/15 text-gold font-medium'
                    : 'text-pearl/50 hover:text-pearl/70'
                }`}
              >
                <Icon size={14} />
                {label}
                {count != null && count > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-gold/20 text-gold text-xs leading-none">
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Email list */}
      <div className="flex-1 overflow-y-auto px-5 sm:px-8 lg:px-12 py-3">
        <div className="max-w-2xl mx-auto space-y-2">
          {loading ? (
            <>
              <SkeletonListItem />
              <SkeletonListItem />
              <SkeletonListItem />
            </>
          ) : emails.length === 0 ? (
            <motion.div variants={item} className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-gold/10 flex items-center justify-center mb-3">
                <Mail size={24} className="text-gold/60" />
              </div>
              <p className="text-pearl/50 text-sm max-w-xs">
                {tab === 'sent' ? t('emptySent') : t('emptyState')}
              </p>
            </motion.div>
          ) : (
            emails.map((email, i) => (
              <motion.div
                key={email.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <EmailRow email={email} onClick={() => setSelectedEmail(email)} />
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Detail sheet */}
      <AnimatePresence>
        {selectedEmail && (
          <EmailDetailSheet
            key={selectedEmail.id}
            email={selectedEmail}
            onClose={() => setSelectedEmail(null)}
            onNavigate={onNavigate}
          />
        )}
      </AnimatePresence>
    </motion.div>
  )
}
